// Assessments guide — the AI Quiz Generator plus the manual question elements
// under Components → Interactive (QuestionGeneratorModal, AssessmentItemsPanel)
// and the over-box question menu (AssessmentBoxMenu).
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const assessmentsGuide = {
  id: "assessments",
  icon: "assignment_turned_in",
  keywords: [
    "quiz",
    "sınav",
    "quiz generator",
    "soru üretici",
    "question",
    "soru",
    "question scene",
    "soru sahnesi",
    "assessment",
    "değerlendirme",
    "test",
    "multiple choice",
    "çoktan seçmeli",
    "çoktan tek seçim",
    "multiple answer",
    "çoktan çok seçim",
    "true false",
    "doğru yanlış",
    "matching",
    "eşleştirme",
    "ordering",
    "sıralama",
    "drag drop",
    "sürükle bırak",
    "fill in the blank",
    "boşluk doldurma",
    "boşluk doldur",
    "free response",
    "açık uçlu",
    "multiple narrations",
    "çoklu anlatım",
    "approve",
    "onayla",
    "history",
    "geçmiş",
    "score",
    "puan",
    "points",
    "partial credit",
    "kısmi puan",
    "feedback",
    "geri bildirim",
    "shuffle",
    "karıştır",
    "evaluación",
    "evaluaciones",
    "generador de preguntas",
    "pregunta",
    "cuestionario",
    "opción múltiple",
    "verdadero falso",
    "emparejamiento",
    "ordenar",
    "arrastrar y soltar",
    "rellenar el espacio",
    "respuesta libre",
    "puntos",
    "crédito parcial",
    "retroalimentación",
    "mezclar respuestas",
  ],
  content: {
    en: {
      feature: "Assessments",
      title: "Assessments & Quiz Generator",
      sections: [
        {
          id: "overview",
          title: "Two ways to add questions",
          videoUrl: null,
          text:
            "Aidea Studio gives you two ways to add assessment questions.\n\n" +
            "- **Quiz Generator** — click **Quiz Generator** (the **?** button) in the AI tools group of the top navbar and let AI write questions from your scenes' narration. On narrower screens it sits inside the **AI** menu.\n" +
            "- **Manual** — open **Components → Interactive** and click or drag a question type onto the canvas, then write it yourself.",
        },
        {
          id: "quiz-generator",
          title: "Using the Quiz Generator",
          videoUrl: null,
          text:
            "The Quiz Generator turns narration into questions. Questions are not written onto the scene you're on: once you approve them, each one is added as a new question scene right after the scene it came from.\n\n" +
            "- On the **Single Scene** tab, the scene list on the left shows your scenes — question scenes are left out and scenes without narration are greyed out. Click a scene to work on it.\n" +
            "- The **Narration Text** box holds a copy of that scene's narration you can edit just for this generation — the scene's real narration doesn't change. **Restore narration text** brings the original back. A scene without narration shows **No sound found in this scene.**\n" +
            "- Under **Narrator Settings**, the voice for the question's audio starts as the scene's own narration voice when it has one. Use **Audio Narration** to pick another one, and the play button to hear the scene's audio.\n" +
            "- Under **Question Type**, use **+** and **−** on each type card to set how many questions of that type you want. You can mix types — for example 2 Multiple Choice and 1 Drag Drop — and the total is shown next to the heading.\n" +
            "- Click **Generate**. Generation runs in the background: you can switch to another scene and start that one too, or close the window — your progress is still there when you reopen it. The scene list shows a spinner while a scene is generating and a count of questions waiting for approval.",
        },
        {
          id: "question-types",
          title: "The question types",
          videoUrl: null,
          text:
            "The Quiz Generator and **Components → Interactive** offer the same 8 question types:\n\n" +
            "- **Multiple Choice** — pick the one correct answer from a list.\n" +
            "- **Multiple Answer** — pick every correct answer from a list.\n" +
            "- **Free Response** — type an open-ended answer; you can set a minimum and maximum length.\n" +
            "- **True/False** — judge a series of statements as true or false.\n" +
            "- **Drag Drop** — drag choices into the correct blanks in the question text.\n" +
            "- **Fill In Blank** — type the missing word or phrase into a blank.\n" +
            "- **Matching** — pair items from two columns.\n" +
            "- **Ordering** — arrange items into the correct sequence.\n\n" +
            "The **Interactive** panel also lists the **Interactive Plugin** (Beta) for interactive scenarios.",
        },
        {
          id: "edit-and-score",
          title: "Editing and scoring a question",
          videoUrl: null,
          text:
            "Once a question is on the canvas, it's a normal editable element.\n\n" +
            "- Click into the question text or an answer to edit it inline, the same way you'd edit any text element.\n" +
            "- Mark the correct answer with the selector next to each option.\n" +
            "- When the question is selected, a small menu appears above it: add or remove an answer, **Fit to screen**, **Duplicate**, **Preview**, **More options** (the gear) and **Delete**.\n" +
            "- **More options** holds the question's settings — depending on the type: the number of answers, **Labels** (A B C or 1 2 3), **Shuffle answers**, **Show feedback**, **Partial credit**, **Buttons** (**Try Again** or **See Answer**) and **Points** (how much the question is worth).\n" +
            "- Button texts, colours and other styling are in the **Settings** tab of the right panel.",
        },
        {
          id: "review-and-approve",
          title: "Reviewing generated questions",
          videoUrl: null,
          text:
            "When generation finishes, the scene's **Generated questions** appear in place of the form, each with its type, text and answers — the correct ones are marked **Correct**.\n\n" +
            "- Every question starts selected. Turn off **Approve this question** on any you don't want.\n" +
            "- **Approve** adds the selected questions to the course and closes the window. **Reject** throws the generated questions away and takes you back to the form.\n" +
            "- Generating again on the same scene adds to the list instead of replacing it.\n" +
            "- Generated questions also show up as waiting for approval in the job widget; approving them in either place adds them only once.",
        },
        {
          id: "multiple-narrations",
          title: "Questions from several scenes",
          videoUrl: null,
          text:
            "The **Multiple Narrations** tab writes questions that combine the narration of several scenes.\n\n" +
            "- In the scene list, switch on **Include in question** for each scene you want to use.\n" +
            "- Each included scene's narration appears as its own editable box (with **Restore narration text**); edits stay in this window.\n" +
            "- Set the question types and counts, then **Generate**. The approved questions are added right after the last included scene.",
        },
        {
          id: "question-history",
          title: "Question history",
          videoUrl: null,
          text:
            "**History** at the top of the Quiz Generator lists the questions generated for this training, as cards labelled with their type and source scene.\n\n" +
            "- Click a card to add that question to the course again.\n" +
            "- The eye icon previews the question with its answers without adding it.\n" +
            "- A green check means the question is already in the training, so it can't be added twice.\n" +
            "- Below the question types, **Questions added to this scene** tells you how many questions from the current scene are already in the course.",
        },
      ],
    },
    tr: {
      feature: "Değerlendirmeler",
      title: "Değerlendirmeler ve Soru Üretici",
      sections: [
        {
          id: "overview",
          title: "İki farklı soru ekleme yolu",
          videoUrl: null,
          text:
            "Aidea Studio, değerlendirme sorusu eklemek için iki yol sunar.\n\n" +
            "- **Soru Üretici** — üst gezinme çubuğundaki yapay zeka araçları grubunda **Soru Üretici**'ye (**?** düğmesi) tıklayın; yapay zeka, sahnelerinizin anlatımından sorular yazsın. Dar ekranlarda bu düğme **AI** menüsünün içinde yer alır.\n" +
            "- **Manuel** — **Bileşenler → Etkileşim**'i açın ve bir soru tipini tuvale tıklayarak veya sürükleyerek ekleyin, ardından soruyu kendiniz yazın.",
        },
        {
          id: "quiz-generator",
          title: "Soru Üretici'yi kullanma",
          videoUrl: null,
          text:
            "Soru Üretici, anlatımı sorulara dönüştürür. Sorular bulunduğunuz sahnenin üzerine yazılmaz: onayladığınızda her biri, kaynak sahnenin hemen ardına yeni bir soru sahnesi olarak eklenir.\n\n" +
            "- **Tek Sahneden** sekmesinde soldaki sahne listesi sahnelerinizi gösterir — soru sahneleri listede yer almaz, anlatımı olmayan sahneler soluk görünür. Üzerinde çalışmak için bir sahneye tıklayın.\n" +
            "- **Anlatım Metni** kutusu, sahnenin anlatımının yalnızca bu üretim için düzenleyebileceğiniz bir kopyasını içerir — sahnenin gerçek anlatımı değişmez. **Anlatım metnini getir** orijinali geri getirir. Anlatımı olmayan bir sahnede **Bu sahnede henüz ses bulunmuyor.** uyarısı görünür.\n" +
            "- **Anlatıcı Ayarları** altında sorunun sesi için, sahnenin kendi anlatım sesi varsa başlangıçta o seçilidir. Başka bir ses seçmek için **Anlatım Sesi**'ni, sahnenin sesini dinlemek için oynat düğmesini kullanın.\n" +
            "- **Soru Tipi** altında her tip kartındaki **+** ve **−** düğmeleriyle o tipten kaç soru istediğinizi belirleyin. Tipleri karıştırabilirsiniz — örneğin 2 Çoktan Tek Seçim ve 1 Sürükle Bırak — toplam sayı başlığın yanında görünür.\n" +
            "- **Oluştur**'a tıklayın. Üretim arka planda sürer: başka bir sahneye geçip onun için de üretim başlatabilir ya da pencereyi kapatabilirsiniz — tekrar açtığınızda ilerlemeniz yerinde durur. Sahne listesinde üretilen sahnenin yanında bir yükleniyor simgesi, onay bekleyen soruların yanında ise sayıları görünür.",
        },
        {
          id: "question-types",
          title: "Soru tipleri",
          videoUrl: null,
          text:
            "Soru Üretici ve **Bileşenler → Etkileşim** aynı 8 soru tipini sunar:\n\n" +
            "- **Çoktan Tek Seçim** — listeden tek doğru cevabı seçin.\n" +
            "- **Çoktan Çok Seçim** — listedeki tüm doğru cevapları seçin.\n" +
            "- **Açık Uçlu** — açık uçlu bir cevap yazın; en az ve en fazla uzunluğu belirleyebilirsiniz.\n" +
            "- **Doğru/Yanlış** — bir dizi ifadeyi doğru ya da yanlış olarak değerlendirin.\n" +
            "- **Sürükle Bırak** — seçenekleri soru metnindeki doğru boşluklara sürükleyin.\n" +
            "- **Boşluk Doldur** — eksik kelimeyi ya da ifadeyi boşluğa yazın.\n" +
            "- **Eşleştirme** — iki sütundaki öğeleri eşleştirin.\n" +
            "- **Sıralama** — öğeleri doğru sıraya dizin.\n\n" +
            "**Etkileşim** panelinde ayrıca etkileşimli senaryolar için **Etkileşim Eklentisi** (Beta) de bulunur.",
        },
        {
          id: "edit-and-score",
          title: "Bir soruyu düzenleme ve puanlama",
          videoUrl: null,
          text:
            "Bir soru tuvale eklendiğinde normal, düzenlenebilir bir öğedir.\n\n" +
            "- Soru metnine veya bir cevaba tıklayarak, diğer metin öğelerinde olduğu gibi yerinde düzenleyin.\n" +
            "- Doğru cevabı, her seçeneğin yanındaki işaretleyiciyle belirleyin.\n" +
            "- Soru seçiliyken üstünde küçük bir menü belirir: cevap ekleme veya çıkarma, **Ekrana sığdır**, **Çoğalt**, **Ön izleme**, **Daha fazla seçenek** (dişli simgesi) ve **Sil**.\n" +
            "- **Daha fazla seçenek** sorunun ayarlarını içerir — soru tipine göre: cevap sayısı, **Etiketler** (A B C veya 1 2 3), **Cevapları karıştır**, **Geri bildirim göster**, **Kısmi puan**, **Düğmeler** (**Tekrar Dene** veya **Cevabı Gör**) ve **Puan** (sorunun kaç puan değerinde olduğu).\n" +
            "- Düğme metinleri, renkler ve diğer stil ayarları sağ paneldeki **Ayarlar** sekmesindedir.",
        },
        {
          id: "review-and-approve",
          title: "Üretilen soruları inceleme",
          videoUrl: null,
          text:
            "Üretim bittiğinde formun yerinde sahnenin **Üretilen sorular** listesi görünür; her soruda tipi, metni ve cevapları yer alır — doğru cevaplar **Doğru** olarak işaretlenir.\n\n" +
            "- Tüm sorular seçili gelir. İstemediğiniz sorularda **Soruyu onaylıyorum** anahtarını kapatın.\n" +
            "- **Onayla**, seçili soruları eğitime ekler ve pencereyi kapatır. **Reddet**, üretilen soruları atar ve sizi forma geri götürür.\n" +
            "- Aynı sahnede yeniden üretim yaptığınızda yeni sorular listeye eklenir, öncekilerin yerini almaz.\n" +
            "- Üretilen sorular iş kuyruğu widget'ında da onay bekliyor olarak görünür; hangisinden onaylarsanız onaylayın yalnızca bir kez eklenir.",
        },
        {
          id: "multiple-narrations",
          title: "Birden fazla sahneden soru",
          videoUrl: null,
          text:
            "**Çoklu Anlatımdan** sekmesi, birkaç sahnenin anlatımını birleştiren sorular yazar.\n\n" +
            "- Sahne listesinde kullanmak istediğiniz her sahne için **Soruya dahil et** anahtarını açın.\n" +
            "- Dahil edilen her sahnenin anlatımı ayrı, düzenlenebilir bir kutuda görünür (**Anlatım metnini getir** ile birlikte); yaptığınız düzenlemeler bu pencerede kalır.\n" +
            "- Soru tiplerini ve sayılarını belirleyip **Oluştur**'a tıklayın. Onaylanan sorular, dahil edilen son sahnenin hemen ardına eklenir.",
        },
        {
          id: "question-history",
          title: "Soru geçmişi",
          videoUrl: null,
          text:
            "Soru Üretici'nin üstündeki **Geçmiş**, bu eğitim için üretilmiş soruları tipleri ve kaynak sahneleriyle etiketlenmiş kartlar halinde listeler.\n\n" +
            "- Bir soruyu eğitime yeniden eklemek için kartına tıklayın.\n" +
            "- Göz simgesi, soruyu eklemeden cevaplarıyla birlikte önizler.\n" +
            "- Yeşil onay işareti sorunun zaten eğitimde olduğunu gösterir; bu yüzden iki kez eklenemez.\n" +
            "- Soru tiplerinin altındaki **Bu sahneye eklenen soru** notu, geçerli sahneden kaç sorunun eğitimde olduğunu gösterir.",
        },
      ],
    },
    es: {
      feature: "Evaluaciones",
      title: "Evaluaciones y Generador de Preguntas",
      sections: [
        {
          id: "overview",
          title: "Dos formas de añadir preguntas",
          videoUrl: null,
          text:
            "Aidea Studio te ofrece dos formas de añadir preguntas de evaluación.\n\n" +
            "- **Generador de Preguntas**: haz clic en **Generador de Preguntas** (el botón **?**) en el grupo de herramientas de IA de la barra de navegación superior y deja que la IA escriba preguntas a partir de la narración de tus escenas. En pantallas más estrechas está dentro del menú **IA**.\n" +
            "- **Manual**: abre **Componentes → Interactivo** y haz clic en un tipo de pregunta o arrástralo al lienzo; después, escríbela tú mismo.",
        },
        {
          id: "quiz-generator",
          title: "Usar el Generador de Preguntas",
          videoUrl: null,
          text:
            "El Generador de Preguntas convierte la narración en preguntas. Las preguntas no se escriben en la escena en la que estás: cuando las apruebas, cada una se añade como una nueva escena de pregunta justo después de la escena de la que procede.\n\n" +
            "- En la pestaña **Una escena**, la lista de la izquierda muestra tus escenas: las escenas de pregunta no aparecen y las escenas sin narración se ven en gris. Haz clic en una escena para trabajar con ella.\n" +
            "- El cuadro **Texto de Narración** contiene una copia de la narración de esa escena que puedes editar solo para esta generación; la narración real de la escena no cambia. **Restaurar texto de narración** recupera el original. Una escena sin narración muestra **No hay sonido en esta escena.**\n" +
            "- En **Configuración del Narrador**, la voz del audio de la pregunta es al principio la propia voz de narración de la escena, si la tiene. Usa **Narración de audio** para elegir otra y el botón de reproducción para escuchar el audio de la escena.\n" +
            "- En **Tipo de Pregunta**, usa **+** y **−** en la tarjeta de cada tipo para indicar cuántas preguntas de ese tipo quieres. Puedes combinar tipos (por ejemplo, 2 de **Una Selección** y 1 de **Arrastrar y Soltar**), y el total aparece junto al encabezado.\n" +
            "- Haz clic en **Generar**. La generación se ejecuta en segundo plano: puedes pasar a otra escena y empezar también con ella, o cerrar la ventana; tu progreso seguirá ahí cuando vuelvas a abrirla. La lista de escenas muestra un indicador de carga mientras se genera una escena y el número de preguntas pendientes de aprobación.",
        },
        {
          id: "question-types",
          title: "Los tipos de pregunta",
          videoUrl: null,
          text:
            "El Generador de Preguntas y **Componentes → Interactivo** ofrecen los mismos 8 tipos de pregunta:\n\n" +
            "- **Una Selección** (opción múltiple): elige la única respuesta correcta de una lista.\n" +
            "- **Selección Múltiple**: elige todas las respuestas correctas de una lista.\n" +
            "- **Respuesta Libre**: escribe una respuesta abierta; puedes fijar una longitud mínima y máxima.\n" +
            "- **Verdadero/Falso**: indica si cada una de una serie de afirmaciones es verdadera o falsa.\n" +
            "- **Arrastrar y Soltar**: arrastra las opciones a los huecos correctos del texto de la pregunta.\n" +
            "- **llenar en Blanco** (rellenar el espacio): escribe en un hueco la palabra o la frase que falta.\n" +
            "- **Emparejamiento**: relaciona elementos de dos columnas.\n" +
            "- **Ordenar**: coloca los elementos en el orden correcto.\n\n" +
            "El panel **Interactivo** también incluye el **Plugin Interactivo** (Beta) para escenarios interactivos.",
        },
        {
          id: "edit-and-score",
          title: "Editar y puntuar una pregunta",
          videoUrl: null,
          text:
            "Una vez en el lienzo, la pregunta es un elemento editable normal.\n\n" +
            "- Haz clic en el texto de la pregunta o en una respuesta para editarlo directamente, igual que cualquier elemento de texto.\n" +
            "- Marca la respuesta correcta con el selector que hay junto a cada opción.\n" +
            "- Con la pregunta seleccionada, aparece un pequeño menú encima: añadir o quitar una respuesta, **Ajustar a la pantalla**, **Duplicar**, **Vista previa**, **Más opciones** (el engranaje) y **Eliminar**.\n" +
            "- **Más opciones** contiene los ajustes de la pregunta; según el tipo: el número de respuestas, **Etiquetas** (A B C o 1 2 3), **Mezclar respuestas**, **Mostrar retroalimentación**, **Crédito parcial**, **Botones** (**Inténtalo de nuevo** o **Ver Respuesta**) y **Puntos** (cuánto vale la pregunta).\n" +
            "- Los textos de los botones, los colores y el resto del estilo están en la pestaña **Ajustes** del panel derecho.",
        },
        {
          id: "review-and-approve",
          title: "Revisar las preguntas generadas",
          videoUrl: null,
          text:
            "Cuando termina la generación, las **Preguntas generadas** de la escena aparecen en lugar del formulario, cada una con su tipo, su texto y sus respuestas; las correctas llevan la marca **Correcta**.\n\n" +
            "- Todas las preguntas empiezan seleccionadas. Desactiva **Aprobar esta pregunta** en las que no quieras.\n" +
            "- **Aprobar** añade las preguntas seleccionadas al curso y cierra la ventana. **Rechazar** descarta las preguntas generadas y te devuelve al formulario.\n" +
            "- Si vuelves a generar en la misma escena, las preguntas nuevas se añaden a la lista en lugar de sustituirla.\n" +
            "- Las preguntas generadas también aparecen como pendientes de aprobación en el widget de trabajos; aprobarlas en cualquiera de los dos sitios las añade una sola vez.",
        },
        {
          id: "multiple-narrations",
          title: "Preguntas a partir de varias escenas",
          videoUrl: null,
          text:
            "La pestaña **Varias narraciones** escribe preguntas que combinan la narración de varias escenas.\n\n" +
            "- En la lista de escenas, activa **Incluir en la pregunta** en cada escena que quieras usar.\n" +
            "- La narración de cada escena incluida aparece en su propio cuadro editable (con **Restaurar texto de narración**); los cambios se quedan en esta ventana.\n" +
            "- Elige los tipos de pregunta y sus cantidades y haz clic en **Generar**. Las preguntas aprobadas se añaden justo después de la última escena incluida.",
        },
        {
          id: "question-history",
          title: "Historial de preguntas",
          videoUrl: null,
          text:
            "**Historial**, en la parte superior del Generador de Preguntas, muestra las preguntas generadas para esta formación como tarjetas con su tipo y su escena de origen.\n\n" +
            "- Haz clic en una tarjeta para volver a añadir esa pregunta al curso.\n" +
            "- El icono del ojo previsualiza la pregunta con sus respuestas sin añadirla.\n" +
            "- Una marca verde indica que la pregunta ya está en la formación, así que no se puede añadir dos veces.\n" +
            "- Debajo de los tipos de pregunta, **Preguntas añadidas a esta escena** indica cuántas preguntas de la escena actual ya están en el curso.",
        },
      ],
    },
  },
};

export default assessmentsGuide;
